// packages/front/src/portfolio/compare-panel.tsx

import {
  isRecord,
  PORTFOLIO_PARTIAL_METRIC_UNAVAILABLE_REASON_OBJ,
  isPortfolioPriceSyncActive,
} from "@signals/shared";
import type { ColumnDef } from "@tanstack/react-table";
import { CalendarDays } from "lucide-react";
import { useMemo, useRef, useState, type FC } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  formatDisplayNumber,
  Loading,
  SmartTable,
  type FilterClause,
  type SortClause,
} from "../_common";
import { usePortfolioCompare, type PortfolioCompareResponse } from "./portfolio.hooks";
import {
  isValuationIncomplete,
  missingValuationReasonLabel,
} from "./portfolio-page-helpers";

type CompareRow = Record<string, unknown>;

const DELTA_METRICS = [
  ["totalValueUSD", "Total Value"],
  ["totalUnrealizedPnLUSD", "Unrealized P&L"],
  ["totalRealizedPnLUSD", "Realized P&L"],
  ["totalDividendsUSD", "Dividends"],
  ["totalCommissionsUSD", "Commissions"],
  ["totalTaxesUSD", "Taxes"],
  ["totalDepositsUSD", "Deposits"],
  ["totalWithdrawalsUSD", "Withdrawals"],
] as const;

function readNumberRecord(value: unknown): Record<string, number> {
  if (!isRecord(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter((entry): entry is [string, number] =>
      typeof entry[1] === "number" && Number.isFinite(entry[1]),
    ),
  );
}

function readRows(value: unknown): CompareRow[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function readPartialMetric(value: unknown): {
  fromUSD: number | null;
  toUSD: number | null;
  deltaUSD: number | null;
  unavailableReason: string | null;
} | null {
  if (!isRecord(value)) return null;
  const readAmount = (amount: unknown) =>
    typeof amount === "number" && Number.isFinite(amount) ? amount : null;
  return {
    fromUSD: readAmount(value["fromUSD"]),
    toUSD: readAmount(value["toUSD"]),
    deltaUSD: readAmount(value["deltaUSD"]),
    unavailableReason: typeof value["unavailableReason"] === "string"
      ? value["unavailableReason"]
      : null,
  };
}

function partialMetricUnavailableLabel(reason: string | null): string {
  switch (reason) {
    case PORTFOLIO_PARTIAL_METRIC_UNAVAILABLE_REASON_OBJ.COVERAGE_CHANGED:
      return "Difference unavailable: the evaluated portion changed between dates.";
    case PORTFOLIO_PARTIAL_METRIC_UNAVAILABLE_REASON_OBJ.COVERAGE_UNKNOWN:
      return "Difference unavailable: coverage is unknown for one or both dates.";
    case PORTFOLIO_PARTIAL_METRIC_UNAVAILABLE_REASON_OBJ.VALUE_UNAVAILABLE:
      return "Partial value is unavailable for one or both dates.";
    default:
      return "Difference unavailable.";
  }
}

function hasMarketValueAtBothDates(row: CompareRow): boolean {
  const fromMarketValueUSD = row["fromMarketValueUSD"];
  const toMarketValueUSD = row["toMarketValueUSD"];
  return (
    typeof fromMarketValueUSD === "number" &&
    Number.isFinite(fromMarketValueUSD) &&
    typeof toMarketValueUSD === "number" &&
    Number.isFinite(toMarketValueUSD)
  );
}

function formatUSD(v: number | undefined): string {
  if (v === undefined || !Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  const formatted = formatDisplayNumber(abs, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `-$${formatted}` : `$${formatted}`;
}

const topMoverColumns: ColumnDef<CompareRow>[] = [
  { accessorKey: "symbolId", header: "Symbol" },
  {
    accessorKey: "changeMarketValue",
    header: "Δ Market Value",
    meta: { align: "right" },
    cell: ({ getValue }) => {
      const value = getValue();
      return formatUSD(typeof value === "number" ? value : undefined);
    },
  },
];

export interface ComparePeriodControlsProps {
  from: string;
  to: string;
  isComparing: boolean;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  onCompare: () => void;
}

interface CompareDateInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

const CompareDateInput: FC<CompareDateInputProps> = ({ label, value, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const openDatePicker = () => {
    const input = inputRef.current;
    if (!input) return;
    try {
      if (typeof input.showPicker === "function") {
        input.showPicker();
        return;
      }
    } catch {
      // Fall back to the browser's native date input interaction.
    }
    input.focus();
    input.click();
  };

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="date"
          aria-label={label}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-8 rounded-md border border-input bg-background px-2 text-sm"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label={`Choose ${label.toLowerCase()} date`}
          title={`Choose ${label.toLowerCase()} date`}
          onClick={openDatePicker}
        >
          <CalendarDays className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export const ComparePeriodControls: FC<ComparePeriodControlsProps> = ({
  from,
  to,
  isComparing,
  onFromChange,
  onToChange,
  onCompare,
}) => (
  <div className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2">
    <span className="mr-1 text-sm font-medium">Compare periods</span>
    <CompareDateInput label="From" value={from} onChange={onFromChange} />
    <CompareDateInput label="To" value={to} onChange={onToChange} />
    <Button
      size="sm"
      className="h-8"
      onClick={onCompare}
      disabled={!from || !to || isComparing}
    >
      {isComparing ? "Comparing..." : "Compare"}
    </Button>
  </div>
);

export interface ComparePeriodResultsProps {
  from: string;
  to: string;
  data: PortfolioCompareResponse | null;
  isLoading: boolean;
  error: string | null;
}

export const ComparePeriodResults: FC<ComparePeriodResultsProps> = ({
  from,
  to,
  data,
  isLoading,
  error,
}) => {
  // SmartTable state — read-only, no pagination (show all rows).
  const [growthFilters, setGrowthFilters] = useState<FilterClause[]>([]);
  const [growthSort, setGrowthSort] = useState<SortClause[]>([]);
  const [declineFilters, setDeclineFilters] = useState<FilterClause[]>([]);
  const [declineSort, setDeclineSort] = useState<SortClause[]>([]);

  const deltas = readNumberRecord(data?.["deltas"]);
  const partialMetricsRaw = data?.["partialMetrics"];
  const partialValue = isRecord(partialMetricsRaw)
    ? readPartialMetric(partialMetricsRaw["partialValueUSD"])
    : null;
  const partialUnrealizedPnL = isRecord(partialMetricsRaw)
    ? readPartialMetric(partialMetricsRaw["partialUnrealizedPnLUSD"])
    : null;
  const topMoversRaw = data?.["topMovers"];
  const growth = isRecord(topMoversRaw)
    ? readRows(topMoversRaw["growth"]).filter(hasMarketValueAtBothDates)
    : [];
  const decline = isRecord(topMoversRaw)
    ? readRows(topMoversRaw["decline"]).filter(hasMarketValueAtBothDates)
    : [];

  const valuationIncomplete = isValuationIncomplete(data);
  const missingValuations = data?.missingValuations ?? [];
  const staleMarketPriceGroups = useMemo(() => {
    const groups = new Map<string, {
      symbol: string;
      valuationDate: string;
      priceDate: string;
      accountNames: Set<string>;
    }>();
    for (const row of readRows(data?.["staleMarketPrices"])) {
      const symbol = row["symbol"];
      const valuationDate = row["valuationDate"];
      const priceDate = row["priceDate"];
      if (typeof symbol !== "string" || typeof valuationDate !== "string" || typeof priceDate !== "string") continue;
      const key = `${symbol}|${valuationDate}|${priceDate}`;
      const group = groups.get(key) ?? {
        symbol,
        valuationDate,
        priceDate,
        accountNames: new Set<string>(),
      };
      if (typeof row["accountName"] === "string") group.accountNames.add(row["accountName"]);
      groups.set(key, group);
    }
    return Array.from(groups.values()).map((group) => ({
      ...group,
      accounts: Array.from(group.accountNames),
    }));
  }, [data]);

  // No pagination — show all rows in a single page.
  const growthPageSize = Math.max(growth.length, 1);
  const declinePageSize = Math.max(decline.length, 1);

  const growthSliced = useMemo(() => growth, [growth]);
  const declineSliced = useMemo(() => decline, [decline]);

  if (!data && !isLoading && !error) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Comparison: {from} — {to}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loading />
        ) : error ? (
          <ErrorState message={error} />
        ) : data ? (
          <div className="mt-4 space-y-4">
            {data.syncStatuses?.some((status) => isPortfolioPriceSyncActive(status.status)) && (
              <p role="status" className="rounded-md border px-3 py-2 text-sm text-muted-foreground">
                FMP history is being checked in the background. This comparison shows the values currently available.
              </p>
            )}
            {valuationIncomplete && (
              <div
                role="status"
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
              >
                <p>
                  Comparison is incomplete because one or more market values are unavailable. Unknown values are shown as —; growth and decline lists only include symbols valued on both dates.
                </p>
                {missingValuations.length > 0 && (
                  <ul className="mt-2 list-inside list-disc space-y-1">
                    {missingValuations.map((item) => (
                      <li key={`${item.symbolId}:${item.reason}`}>
                        <span className="font-medium">
                          {item.symbol || item.name || item.symbolId || "Unknown instrument"}
                        </span>
                        {`: ${missingValuationReasonLabel(item.reason)}`}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {staleMarketPriceGroups.length > 0 && (
              <div
                role="status"
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
              >
                <p>Some positions are valued using market prices older than 7 calendar days:</p>
                <ul className="mt-2 list-inside list-disc space-y-1">
                  {staleMarketPriceGroups.map((item) => (
                    <li key={`${item.symbol}:${item.valuationDate}:${item.priceDate}`}>
                      <span className="font-medium">{item.symbol}</span>
                      {`: price from ${item.priceDate} for ${item.valuationDate}`}
                      {item.accounts.length > 0 ? ` (${item.accounts.join(", ")})` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <h4 className="mb-2 text-sm font-medium">Deltas</h4>
              <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-3">
                {DELTA_METRICS.map(([key, label]) => (
                  <div key={key} className="flex justify-between rounded border px-3 py-2">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-medium">{formatUSD(deltas[key])}</span>
                  </div>
                ))}
              </div>
            </div>

            {valuationIncomplete && (partialValue || partialUnrealizedPnL) && (
              <div>
                <h4 className="mb-2 text-sm font-medium">Partial valuation (USD)</h4>
                <div className="space-y-2 text-sm">
                  {([
                    ["Partial Value", partialValue],
                    ["Partial Unrealized P&L", partialUnrealizedPnL],
                  ] as const).map(([label, metric]) => metric && (
                    <div key={label} className="rounded border px-3 py-2">
                      <div className="grid grid-cols-4 gap-2">
                        <span className="text-muted-foreground">{label}</span>
                        <span>From: {formatUSD(metric.fromUSD ?? undefined)}</span>
                        <span>To: {formatUSD(metric.toUSD ?? undefined)}</span>
                        <span>Δ: {formatUSD(metric.deltaUSD ?? undefined)}</span>
                      </div>
                      {metric.deltaUSD === null && (
                        <p className="mt-1 text-xs text-amber-800">
                          {partialMetricUnavailableLabel(metric.unavailableReason)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <h4 className="mb-2 text-sm font-medium text-emerald-600">Top Growth</h4>
                <SmartTable
                  columns={topMoverColumns}
                  data={growthSliced}
                  total={growth.length}
                  page={1}
                  pageSize={growthPageSize}
                  isLoading={false}
                  error={null}
                  filters={growthFilters}
                  sort={growthSort}
                  onPageChange={() => {}}
                  onPageSizeChange={() => {}}
                  onFiltersChange={setGrowthFilters}
                  onSortChange={setGrowthSort}
                  rowIdField="symbolId"
                />
              </div>
              <div>
                <h4 className="mb-2 text-sm font-medium text-red-600">Top Decline</h4>
                <SmartTable
                  columns={topMoverColumns}
                  data={declineSliced}
                  total={decline.length}
                  page={1}
                  pageSize={declinePageSize}
                  isLoading={false}
                  error={null}
                  filters={declineFilters}
                  sort={declineSort}
                  onPageChange={() => {}}
                  onPageSizeChange={() => {}}
                  onFiltersChange={setDeclineFilters}
                  onSortChange={setDeclineSort}
                  rowIdField="symbolId"
                />
              </div>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

/**
 * Combined version retained for isolated consumers; the portfolio page renders
 * controls and results separately around the Summary card.
 */
export const ComparePanel: FC<{ portfolioId?: string }> = ({
  portfolioId = "",
}) => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [hasCompared, setHasCompared] = useState(false);
  const compareQuery = usePortfolioCompare(
    portfolioId,
    from,
    to,
    hasCompared && Boolean(from) && Boolean(to),
  );

  return (
    <>
      <ComparePeriodControls
        from={from}
        to={to}
        isComparing={hasCompared && compareQuery.isFetching}
        onFromChange={(value) => {
          setFrom(value);
          setHasCompared(false);
        }}
        onToChange={(value) => {
          setTo(value);
          setHasCompared(false);
        }}
        onCompare={() => setHasCompared(true)}
      />
      <ComparePeriodResults
        from={from}
        to={to}
        data={hasCompared ? (compareQuery.data ?? null) : null}
        isLoading={hasCompared && compareQuery.isLoading}
        error={hasCompared ? (compareQuery.error?.message ?? null) : null}
      />
    </>
  );
};
